import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  FileText, 
  TrendingUp,
  Users,
  Calculator,
  IndianRupee,
  Bell
} from 'lucide-react'

const AccountantReports: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-gray-600">Generate and download comprehensive financial reports</p>
        </div>
        <Button>
          <FileText className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <IndianRupee className="h-5 w-5 text-emerald-600" />
              <span>Revenue Report</span>
            </CardTitle>
            <CardDescription>Comprehensive revenue analysis and collection summary</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Period</span>
                <Badge variant="outline">Monthly/Yearly</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Format</span>
                <Badge variant="outline">PDF/Excel</Badge>
              </div>
              <Button size="sm" className="w-full">Download Revenue Report</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span>Outstanding Dues</span>
            </CardTitle>
            <CardDescription>Students with pending fee payments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Filter</span>
                <Badge variant="outline">By Program/Year</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Sort</span>
                <Badge variant="outline">Amount/Date</Badge>
              </div>
              <Button size="sm" className="w-full">Download Dues Report</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <span>Analytics Report</span>
            </CardTitle>
            <CardDescription>Detailed financial analytics and trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Charts</span>
                <Badge variant="outline">Visual Analytics</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Data</span>
                <Badge variant="outline">Comprehensive</Badge>
              </div>
              <Button size="sm" className="w-full">Download Analytics</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calculator className="h-5 w-5 text-orange-600" />
              <span>Payment Summary</span>
            </CardTitle>
            <CardDescription>Detailed payment transactions and modes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Timeframe</span>
                <Badge variant="outline">Custom Range</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Details</span>
                <Badge variant="outline">Transaction Level</Badge>
              </div>
              <Button size="sm" className="w-full">Download Payments</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-teal-600" />
              <span>Audit Report</span>
            </CardTitle>
            <CardDescription>Complete financial audit trail</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Scope</span>
                <Badge variant="outline">Full Audit</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Compliance</span>
                <Badge variant="outline">Standard Format</Badge>
              </div>
              <Button size="sm" className="w-full">Download Audit</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Bell className="h-5 w-5 text-red-600" />
              <span>Reminder Report</span>
            </CardTitle>
            <CardDescription>Payment reminders and follow-up list</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Priority</span>
                <Badge variant="outline">High/Medium/Low</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Contact</span>
                <Badge variant="outline">Phone/Email</Badge>
              </div>
              <Button size="sm" className="w-full">Download Reminders</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  )
}

export default AccountantReports
